import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, History } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Card, CardContent, CardHeader } from '@/components/ui';
import { pipelineApi, type CommitHistoryRow, type CommitDetailResponse } from '@/lib/api';
import { cn } from '@/lib/cn';

/**
 * Compact history of past morning commits + per-batch drill-down.
 *
 * One row per committed batch with a chevron that expands an inline
 * detail panel listing the documents that contributed and the parsed
 * triples (subject / predicate / object). Reads the new
 * /api/v1/pipeline/commits endpoints — Turtle is parsed server-side
 * via rdflib so the response is already display-ready.
 */
export function CommitHistoryCard() {
  const { t, i18n } = useTranslation(['studio', 'common']);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['studio', 'commits'],
    queryFn: () => pipelineApi.commits(50),
    refetchInterval: 30_000,
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-slate-500" aria-hidden />
          <h2 className="text-h2 text-brand-900">{t('studio:morning.commitHistory.title')}</h2>
        </div>
        <p className="mt-1 text-small text-slate-600">
          {t('studio:morning.commitHistory.subtitle')}
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading && (
          <p className="px-4 py-3 text-small text-slate-500">{t('common:status.loading')}</p>
        )}
        {isError && (
          <p className="px-4 py-3 text-small text-red-700">{t('common:status.error')}</p>
        )}
        {!isLoading && data && data.length === 0 && (
          <p className="px-4 py-6 text-center text-body text-slate-500">
            {t('studio:morning.commitHistory.empty')}
          </p>
        )}
        {data && data.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {data.map((row) => (
              <CommitRow
                key={row.batch_id}
                row={row}
                expanded={expanded === row.batch_id}
                onToggle={() =>
                  setExpanded((cur) => (cur === row.batch_id ? null : row.batch_id))
                }
                locale={i18n.language}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

interface CommitRowProps {
  row: CommitHistoryRow;
  expanded: boolean;
  onToggle: () => void;
  locale: string;
}

function CommitRow({ row, expanded, onToggle, locale }: CommitRowProps) {
  const { t } = useTranslation('studio');

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-slate-50"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-400" aria-hidden />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-body text-slate-900">
            {new Date(row.committed_at).toLocaleString(locale)}
          </p>
          <p className="truncate font-mono text-small text-slate-500">{row.batch_id}</p>
        </div>
        <div className="flex items-center gap-2 text-small">
          <Badge variant="success">
            {t('morning.commitHistory.triples', { count: row.triplets_staged })}
          </Badge>
          <Badge variant="neutral">
            {t('morning.commitHistory.docs', { count: row.documents_processed })}
          </Badge>
          {row.documents_halted > 0 && (
            <Badge variant="warning">
              {t('morning.commitHistory.halted', { count: row.documents_halted })}
            </Badge>
          )}
        </div>
      </button>
      {expanded && <CommitDetail batchId={row.batch_id} />}
    </li>
  );
}

function CommitDetail({ batchId }: { batchId: string }) {
  const { t } = useTranslation(['studio', 'common']);
  const [tab, setTab] = useState<'triples' | 'docs'>('triples');

  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'commits', batchId, 'detail'],
    queryFn: () => pipelineApi.commitDetail(batchId, 500),
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return <p className="px-12 py-3 text-small text-slate-500">{t('common:status.loading')}</p>;
  }
  if (!data) return null;

  return (
    <div className="bg-slate-50 px-4 py-3">
      <div className="mb-2 flex gap-1">
        <TabBtn active={tab === 'triples'} onClick={() => setTab('triples')}>
          {t('studio:morning.commitHistory.triplesTab', {
            count: data.triplets_total,
          })}
        </TabBtn>
        <TabBtn active={tab === 'docs'} onClick={() => setTab('docs')}>
          {t('studio:morning.commitHistory.docsTab', { count: data.documents.length })}
        </TabBtn>
      </div>

      {tab === 'triples' && <TriplesTable detail={data} />}
      {tab === 'docs' && <DocsTable detail={data} />}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-3 py-1 text-small transition-colors',
        active
          ? 'bg-white font-medium text-brand-900 shadow-sm'
          : 'text-slate-600 hover:text-slate-900',
      )}
    >
      {children}
    </button>
  );
}

function TriplesTable({ detail }: { detail: CommitDetailResponse }) {
  const { t } = useTranslation('studio');

  if (detail.triples.length === 0) {
    return (
      <p className="py-2 text-small text-slate-500">
        {t('morning.commitHistory.noTriples')}
      </p>
    );
  }
  return (
    <>
      <div className="max-h-80 overflow-auto rounded border border-slate-200 bg-white">
        <table className="w-full text-small">
          <thead className="sticky top-0 bg-slate-100 text-h4 text-slate-600">
            <tr>
              <th className="px-3 py-1 text-left">{t('morning.commitHistory.cols.subject')}</th>
              <th className="px-3 py-1 text-left">
                {t('morning.commitHistory.cols.predicate')}
              </th>
              <th className="px-3 py-1 text-left">{t('morning.commitHistory.cols.object')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 font-mono">
            {detail.triples.map((tri, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-3 py-1 text-brand-900">{tri.subject}</td>
                <td className="px-3 py-1 text-accent-700">{tri.predicate}</td>
                <td className="px-3 py-1 text-slate-700">{tri.object}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detail.truncated && (
        <p className="mt-1 text-small text-slate-500">
          {t('morning.commitHistory.truncated', { shown: detail.triples.length })}
        </p>
      )}
    </>
  );
}

function DocsTable({ detail }: { detail: CommitDetailResponse }) {
  const { t } = useTranslation('studio');

  return (
    <div className="rounded border border-slate-200 bg-white">
      <table className="w-full text-small">
        <thead className="bg-slate-100 text-h4 text-slate-600">
          <tr>
            <th className="px-3 py-1 text-left">{t('morning.commitHistory.cols.source')}</th>
            <th className="px-3 py-1 text-right">
              {t('morning.commitHistory.cols.triplets')}
            </th>
            <th className="px-3 py-1 text-right">
              {t('morning.commitHistory.cols.entities')}
            </th>
            <th className="px-3 py-1 text-left">
              {t('morning.commitHistory.cols.warnings')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {detail.documents.map((doc) => (
            <tr key={doc.run_id} className="hover:bg-slate-50">
              <td className="max-w-md truncate px-3 py-1 font-mono text-slate-700">
                {doc.source_url || doc.source_id}
              </td>
              <td className="px-3 py-1 text-right text-slate-900">{doc.triplets_count}</td>
              <td className="px-3 py-1 text-right text-slate-900">{doc.entity_count}</td>
              <td className="px-3 py-1">
                {doc.with_warnings ? (
                  <Badge variant="warning">⚠</Badge>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
